import type { EnemyRoleId } from "../content/schemas";
import { createRng, hashSeed, rngRange } from "./rng";

export type IncursionWaveId = "breacher_rush" | "elite_heavy" | "skirmisher_swarm" | "turret_line";

export type IncursionState = {
  nextAt: number;
  active: boolean;
  activeTimer: number;
  waveId: IncursionWaveId | null;
  budgetMult: number;
};

export const INCURSION_MIN_S = 75;
export const INCURSION_MAX_S = 105;
export const INCURSION_DURATION_S = 14;
export const INCURSION_BUDGET_MULT = 1.25;

export const INCURSION_LABELS: Record<IncursionWaveId, string> = {
  breacher_rush: "BREACHER RUSH",
  elite_heavy: "ELITE HEAVY",
  skirmisher_swarm: "SKIRMISHER SWARM",
  turret_line: "TURRET LINE",
};

export function createIncursionState(seed: number): IncursionState {
  return {
    nextAt: scheduleNextIncursion(0, seed, 0),
    active: false,
    activeTimer: 0,
    waveId: null,
    budgetMult: 1,
  };
}

export function scheduleNextIncursion(simTime: number, seed: number, tick: number): number {
  const rng = createRng(hashSeed(seed, tick + Math.floor(simTime * 10) + 991));
  return simTime + rngRange(rng, INCURSION_MIN_S, INCURSION_MAX_S);
}

export function rollIncursionWave(seed: number, tick: number): IncursionWaveId {
  const rng = createRng(hashSeed(seed, tick * 17 + 404));
  const roll = rng();
  if (roll < 0.28) return "breacher_rush";
  if (roll < 0.52) return "elite_heavy";
  if (roll < 0.78) return "skirmisher_swarm";
  return "turret_line";
}

export type IncursionSpawnSpec = { role: EnemyRoleId; elite: boolean };

export function incursionSpawnBatch(waveId: IncursionWaveId, level: number): IncursionSpawnSpec[] {
  switch (waveId) {
    case "breacher_rush":
      return Array.from({ length: level >= 10 ? 3 : 2 }, () => ({
        role: "breacher" as const,
        elite: false,
      }));
    case "elite_heavy":
      return [
        { role: "skirmisher" as const, elite: true },
        ...(level >= 8 ? [{ role: "bomber" as const, elite: true }] : []),
      ];
    case "skirmisher_swarm":
      return Array.from({ length: level >= 12 ? 3 : 2 }, () => ({
        role: "skirmisher" as const,
        elite: level >= 14,
      }));
    case "turret_line":
      return [
        { role: "turret" as const, elite: false },
        { role: "scout" as const, elite: false },
        ...(level >= 12 ? [{ role: "turret" as const, elite: true }] : []),
      ];
    default:
      return [{ role: "scout" as const, elite: false }];
  }
}

/** Returns start/end signals for incursion lifecycle. */
export function tickIncursion(
  state: IncursionState,
  simTime: number,
  dt: number,
  seed: number,
  tick: number,
): "start" | "end" | null {
  if (state.active) {
    state.activeTimer -= dt;
    if (state.activeTimer <= 0) {
      state.active = false;
      state.waveId = null;
      state.budgetMult = 1;
      state.nextAt = scheduleNextIncursion(simTime, seed, tick);
      return "end";
    }
    return null;
  }
  if (simTime >= state.nextAt) return "start";
  return null;
}

export function startIncursion(state: IncursionState, _simTime: number, waveId: IncursionWaveId): void {
  state.active = true;
  state.activeTimer = INCURSION_DURATION_S;
  state.waveId = waveId;
  state.budgetMult = INCURSION_BUDGET_MULT;
}
